import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../api/supabase'
import { validateHSCodesWithHMRC } from '../../api/hmrcValidator'

export default function HSCodeInspectionLog() {
  const navigate = useNavigate()
  const [records, setRecords] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    job_ref: '',
    importer_name: '',
    hs_codes_inspected: '',
    inspection_type: 'physical',
    result: 'cleared',
    officer_name: '',
    notes: ''
  })
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    result: '',
    inspectionType: ''
  })
  const [hsSearchInput, setHsSearchInput] = useState('')
  const [hsSearchResults, setHsSearchResults] = useState<any[]>([])
  const [hsSearchLoading, setHsSearchLoading] = useState(false)

  useEffect(() => {
    loadRecords()
  }, [filters])

  async function loadRecords() {
    setLoading(true)
    try {
      let query = supabase.from('hs_code_inspections').select('*')

      if (filters.dateFrom) {
        query = query.gte('date', filters.dateFrom)
      }
      if (filters.dateTo) {
        query = query.lte('date', filters.dateTo)
      }
      if (filters.result) {
        query = query.eq('result', filters.result)
      }
      if (filters.inspectionType) {
        query = query.eq('inspection_type', filters.inspectionType)
      }

      const { data, error } = await query.order('date', { ascending: false })

      if (error) throw error
      setRecords(data || [])
    } catch (error) {
      console.error('Error loading records:', error)
    } finally {
      setLoading(false)
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    try {
      const { error } = await supabase.from('hs_code_inspections').insert([{
        ...formData,
        created_by: 'user@example.com'
      }])

      if (error) throw error
      setShowForm(false)
      setFormData({
        date: new Date().toISOString().split('T')[0],
        job_ref: '',
        importer_name: '',
        hs_codes_inspected: '',
        inspection_type: 'physical',
        result: 'cleared',
        officer_name: '',
        notes: ''
      })
      loadRecords()
    } catch (error) {
      console.error('Error saving record:', error)
    }
  }

  async function handleHsSearch() {
    if (!hsSearchInput.trim()) return
    setHsSearchLoading(true)
    try {
      const codes = hsSearchInput.split(/[,\n]/).map(c => c.trim()).filter(c => c)
      const results = await validateHSCodesWithHMRC(codes)
      setHsSearchResults(results)
    } catch (error) {
      console.error('Error searching codes:', error)
    } finally {
      setHsSearchLoading(false)
    }
  }

  const thisMonth = new Date()
  const monthStart = new Date(thisMonth.getFullYear(), thisMonth.getMonth(), 1).toISOString().split('T')[0]
  const monthInspections = records.filter(r => r.date >= monthStart)
  const heldCount = monthInspections.filter(r => r.result === 'held' || r.result === 'seized').length
  const holdRate = monthInspections.length > 0 ? ((heldCount / monthInspections.length) * 100).toFixed(1) : '0'

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
          <button
            onClick={() => navigate('/customs')}
            className="hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
          >
            Customs
          </button>
          <span>/</span>
          <span className="text-gray-900 dark:text-white">HS Code Inspection Log</span>
        </div>

        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">HS Code Inspection Log</h1>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            {showForm ? 'Cancel' : '+ New Inspection'}
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <p className="text-gray-600 dark:text-gray-400 text-sm">This Month</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{monthInspections.length}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <p className="text-gray-600 dark:text-gray-400 text-sm">Hold Rate</p>
            <p className="text-2xl font-bold text-red-600 dark:text-red-400">{holdRate}%</p>
          </div>
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <p className="text-gray-600 dark:text-gray-400 text-sm">Total Records</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{records.length}</p>
          </div>
        </div>

        {/* Form */}
        {showForm && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Record New Inspection</h2>

            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">HS Code Search Widget</h3>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={hsSearchInput}
                  onChange={(e) => setHsSearchInput(e.target.value)}
                  placeholder="Enter HS codes (comma or line-separated)"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <button
                  onClick={handleHsSearch}
                  disabled={hsSearchLoading}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:bg-gray-400"
                >
                  {hsSearchLoading ? 'Searching...' : 'Lookup'}
                </button>
              </div>
              {hsSearchResults.length > 0 && (
                <div className="mt-3 max-h-40 overflow-y-auto">
                  {hsSearchResults.map((result) => (
                    <div key={result.hs_code} className="text-sm py-1 px-2 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                      <span className="font-mono font-semibold text-gray-900 dark:text-white">{result.hs_code}</span>
                      <span className="text-gray-600 dark:text-gray-400 ml-2">{result.official_description || result.error}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Job Reference</label>
                  <input
                    type="text"
                    value={formData.job_ref}
                    onChange={(e) => setFormData({...formData, job_ref: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Importer Name</label>
                  <input
                    type="text"
                    value={formData.importer_name}
                    onChange={(e) => setFormData({...formData, importer_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Inspection Type</label>
                  <select
                    value={formData.inspection_type}
                    onChange={(e) => setFormData({...formData, inspection_type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="physical">Physical</option>
                    <option value="documentary">Documentary</option>
                    <option value="scanning">Scanning</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Result</label>
                  <select
                    value={formData.result}
                    onChange={(e) => setFormData({...formData, result: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="cleared">Cleared</option>
                    <option value="held">Held</option>
                    <option value="seized">Seized</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Officer Name</label>
                  <input
                    type="text"
                    value={formData.officer_name}
                    onChange={(e) => setFormData({...formData, officer_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">HS Codes (comma-separated)</label>
                <input
                  type="text"
                  value={formData.hs_codes_inspected}
                  onChange={(e) => setFormData({...formData, hs_codes_inspected: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  rows={3}
                />
              </div>
              <button
                type="submit"
                className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Save Inspection Record
              </button>
            </form>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">From Date</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">To Date</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Result</label>
              <select
                value={filters.result}
                onChange={(e) => setFilters({...filters, result: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">All</option>
                <option value="cleared">Cleared</option>
                <option value="held">Held</option>
                <option value="seized">Seized</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Type</label>
              <select
                value={filters.inspectionType}
                onChange={(e) => setFilters({...filters, inspectionType: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">All</option>
                <option value="physical">Physical</option>
                <option value="documentary">Documentary</option>
                <option value="scanning">Scanning</option>
              </select>
            </div>
          </div>
        </div>

        {/* Records Table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">Date</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">Job Ref</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">Importer</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">HS Codes</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">Type</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">Result</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">Officer</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">Loading...</td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">No inspections recorded</td>
                </tr>
              ) : (
                records.map((record) => (
                  <tr key={record.id} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                    <td className="px-4 py-3 text-gray-900 dark:text-white">{new Date(record.date).toLocaleDateString()}</td>
                    <td className="px-4 py-3 font-mono text-gray-900 dark:text-white">{record.job_ref}</td>
                    <td className="px-4 py-3 text-gray-900 dark:text-white">{record.importer_name}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-xs">{record.hs_codes_inspected}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded text-xs font-medium">
                        {record.inspection_type}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        record.result === 'cleared' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                        record.result === 'held' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400' :
                        'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                      }`}>
                        {record.result}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-900 dark:text-white">{record.officer_name}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
